// apps/web/src/features/clients/useClientFieldError.ts
//
// Localized copy for the client editors' field-level validation failures
// (AC-8). The shared Zod schemas emit developer English — `invalid INN`,
// `Required text must not be blank`, `String must contain at most N
// character(s)` — and React Hook Form renders those verbatim, which is how bare
// English reached the Russian UI.
//
// WHY not string-matching every message: the mapping keys off the ZOD ISSUE
// CODE (`error.type`) wherever one exists, and on the two shared custom-refine
// literals only where the code alone (`custom`) cannot tell the fields apart.
// Mirrors the existing `shared/phoneError.ts` convention: an unrecognised
// message is passed through untouched, so a server-supplied (already localized)
// `setError` message is never swallowed.
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldError } from 'react-hook-form';
import { ClientLegalEntityType } from '@magimp/shared';

/** Raw messages emitted by the shared client schemas. */
const RAW_BLANK = 'Required text must not be blank';
const RAW_INVALID_INN = 'invalid INN';

export interface ClientFieldErrorOptions {
  /** `inn` gets type-bound guidance; `text` gets required/too-long copy. */
  kind: 'text' | 'inn';
  /** Wire bound for the field, quoted back in the too-long message. */
  max?: number;
  /** Legal form the INN must match, when the form knows it. */
  legalType?: ClientLegalEntityType;
}

/**
 * @aiPurpose translate one RHF field error into localized, actionable copy.
 * @aiInvariant returns the original message for anything it does not
 *   recognise — including messages a mutation handler set itself, which are
 *   already localized.
 */
export function useClientFieldError() {
  const { t } = useTranslation(['clients']);
  return useCallback(
    (error: FieldError | undefined, options: ClientFieldErrorOptions): string | undefined => {
      if (!error) return undefined;
      const message = error.message;

      if (options.kind === 'inn') {
        // Empty, non-numeric, wrong length and failed checksum all land here;
        // the one thing the operator needs is what a valid INN looks like for
        // THIS legal form.
        if (message === RAW_INVALID_INN || error.type === 'custom' || error.type === 'too_small') {
          if (options.legalType === ClientLegalEntityType.OOO) return t('clients:errors.innOoo');
          if (options.legalType === ClientLegalEntityType.IP) return t('clients:errors.innIp');
          return t('clients:errors.innAny');
        }
        return message;
      }

      if (error.type === 'too_big') {
        return options.max === undefined
          ? t('clients:errors.tooLongUnbounded')
          : t('clients:errors.tooLong', { max: options.max });
      }
      if (error.type === 'too_small' || message === RAW_BLANK) return t('clients:errors.required');
      return message;
    },
    [t],
  );
}
